import React, { useEffect, useState, useMemo } from "react";
import { Card, Button, Modal, Table } from "react-bootstrap";
import { db } from "../../firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { fmtInr, fmtInrPdf } from "../../utils/eventUtils";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

// Palette for consistent colors (adjust as needed)
const palette = {
  blue: [25, 118, 210],        // header blue
  lightBlue: [227, 242, 253],  // summary band background
  white: [255, 255, 255],
  black: [0, 0, 0],
  green: [46, 125, 50],
  red: [198, 40, 40],
};

// Case-insensitive string compare
const cmpStr = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });

export default function OverviewPanel({ event, community }) {
  const eventId = event?.id;
  const [funds, setFunds] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Live data
  useEffect(() => {
    if (!eventId) return;

    const qFunds = query(
      collection(db, "events", eventId, "funds"),
      orderBy("createdAt", "desc")
    );
    const unsubFunds = onSnapshot(qFunds, (snap) => {
      setFunds(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const qExp = query(
      collection(db, "events", eventId, "expenses"),
      orderBy("createdAt", "desc")
    );
    const unsubExp = onSnapshot(qExp, (snap) => {
      setExpenses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubFunds();
      unsubExp();
    };
  }, [eventId]);

  // Split funds
  const residentFunds = useMemo(
    () => funds.filter((f) => (f.sourceType || "resident") === "resident"),
    [funds]
  );
  const otherFunds = useMemo(
    () => funds.filter((f) => (f.sourceType || "resident") === "other"),
    [funds]
  );

  const totalFunds = funds.reduce((a, b) => a + (Number(b.amount) || 0), 0);
  const totalExpenses = expenses.reduce((a, b) => a + (Number(b.amount) || 0), 0);
  const balance = totalFunds - totalExpenses;

  /* ----------------------- Grouping Helpers ----------------------- */

  const groupResidentFundsByBlock = () => {
    const map = new Map();
    residentFunds.forEach((f) => {
      const block = (f.blockName || "—").toString();
      const arr = map.get(block) || [];
      arr.push(f);
      map.set(block, arr);
    });
    return Array.from(map.entries()).sort((a, b) => cmpStr(a[0], b[0]));
  };

  const groupExpensesByCategory = () => {
    const map = new Map();
    (expenses || []).forEach((e) => {
      const cat = (e.category || "Uncategorized").toString().trim() || "Uncategorized";
      const arr = map.get(cat) || [];
      arr.push(e);
      map.set(cat, arr);
    });
    return Array.from(map.entries()).sort((a, b) => cmpStr(a[0], b[0]));
  };

  const groupCommitteeByBlock = () => {
    const committee = Array.isArray(event?.committee) ? event.committee : [];
    const map = new Map();
    committee.forEach((m) => {
      const block = (m.blockName || "—").toString();
      const arr = map.get(block) || [];
      arr.push(m);
      map.set(block, arr);
    });
    return Array.from(map.entries()).sort((a, b) => cmpStr(a[0], b[0]));
  };

  // ✅ Event URL for the footer (clickable)
  const getEventUrl = () => {
    const base = window.location.origin; // will be https://lupi.bigpillar.com in production;
    return event?.publicUrl || `${base}/community/${community?.id || community?.slug || "UNKNOWN"}/events/${event?.id || ""}`;
  };

  /* ----------------------- PDF Writers ----------------------- */

  const writeHeader = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`${event?.name || "Event"} — Summary`, 40, 40);

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    let y = 64;
    doc.text(`Community: ${community?.name || "—"}`, 40, y);
    y += 16;
    doc.text(`Date: ${event?.date || "—"}`, 40, y);
    y += 16;

    const desc = `Description: ${event?.description || "—"}`;
    const descLines = doc.splitTextToSize(desc, pageWidth - 80);
    doc.text(descLines, 40, y);
    y += descLines.length * 14 + 10;

    return y;
  };

  const writeSummaryBand = (doc, yStart) => {
    const pageWidth = doc.internal.pageSize.getWidth();

    const fundsStr = fmtInrPdf(totalFunds);
    const expStr = fmtInrPdf(totalExpenses);
    const balStr = fmtInrPdf(balance);

    const bandX = 40;
    const bandY = yStart + 10;
    const bandW = pageWidth - 80;
    const bandH = 70;

    doc.setFillColor(...palette.lightBlue);
    doc.roundedRect(bandX, bandY, bandW, bandH, 8, 8, "F");

    const colW = bandW / 3;
    const headingY = bandY + 22;
    const valueY = bandY + 45;

    doc.setFont("helvetica", "normal");
    doc.setTextColor(120);
    doc.setFontSize(11);
    doc.text("Total Funds", bandX + colW * 0 + colW / 2, headingY, { align: "center" });
    doc.text("Total Expenses", bandX + colW * 1 + colW / 2, headingY, { align: "center" });
    doc.text("Balance", bandX + colW * 2 + colW / 2, headingY, { align: "center" });

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");

    doc.setTextColor(...palette.green);
    doc.text(fundsStr, bandX + colW * 0 + colW / 2, valueY, { align: "center" });

    doc.setTextColor(...palette.red);
    doc.text(expStr, bandX + colW * 1 + colW / 2, valueY, { align: "center" });

    const balColor = balance >= 0 ? palette.green : palette.red;
    doc.setTextColor(...balColor);
    doc.text(balStr, bandX + colW * 2 + colW / 2, valueY, { align: "center" });

    doc.setTextColor(...palette.black);
    return bandY + bandH + 20;
  };

  const writeBlockWiseSummaryTable = (doc, yStart) => {
    const grouped = groupResidentFundsByBlock();
    const rows = grouped.map(([block, items]) => {
      const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
      return [block, fmtInrPdf(total), String(items.length)];
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Block-wise Collection", 40, yStart);
    const startY = yStart + 10;

    autoTable(doc, {
      startY,
      head: [["Block", "Total Amount", "Entries"]],
      body: rows,
      styles: {
        fontSize: 9,
        cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: palette.blue,
        textColor: palette.white,
        halign: "center",
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right" },
        2: { halign: "center" },
      },
      theme: "striped",
      margin: { left: 40, right: 40 },
      tableWidth: "auto",
      showHead: "everyPage",
    });

    return (doc.lastAutoTable && doc.lastAutoTable.finalY) || startY + 5;
  };

  // ✅ Category-wise Expenses table WITH compact purposes row under each category
  const writeExpensesByCategoryTable = (doc, startTitleY, { forceNewPage = false } = {}) => {
    if (forceNewPage) doc.addPage();

    const grouped = groupExpensesByCategory();

    // Build table rows: for each category add 2 rows
    const body = [];
    grouped.forEach(([cat, items]) => {
      const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
      const entries = items.length;

      // 1) Summary row (Category | Total | Entries)
      body.push([cat, fmtInrPdf(total), String(entries)]);

      // 2) Compact “Purposes” details row (spans all 3 columns)
      const purposeList = items
        .map((it) => (it.purpose || "—").toString().trim())
        .filter((p) => p && p !== "—");

      const purposesText =
        purposeList.length > 0
          ? `Purposes: • ${purposeList.join(" • ")}`
          : "Purposes: —";

      body.push([
        {
          content: purposesText,
          colSpan: 3,
          styles: {
            fontSize: 8,
            textColor: 100, // grey
            cellPadding: { top: 2, right: 6, bottom: 6, left: 6 },
          },
        },
      ]);
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Expenses — Category-wise", 40, startTitleY);

    autoTable(doc, {
      startY: startTitleY + 10,
      head: [["Category", "Total Amount", "Entries"]],
      body,
      styles: {
        fontSize: 10,
        cellPadding: { top: 6, right: 8, bottom: 6, left: 8 },
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: palette.blue,
        textColor: palette.white,
        halign: "center",
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right" },
        2: { halign: "center" },
      },
      theme: "striped",
      margin: { left: 40, right: 40 },
      tableWidth: "auto",
      showHead: "everyPage",
    });

    return (doc.lastAutoTable && doc.lastAutoTable.finalY) || startTitleY + 15;
  };

  // Detailed funds by block (for Full Statement)
  const writeFullFundsByBlock = (doc, yStart) => {
    let y = yStart;
    const grouped = groupResidentFundsByBlock();
    if (grouped.length === 0) return y;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Funds — Detailed by Block", 40, y);
    y += 12;

    grouped.forEach(([block, items], idx) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(`Block: ${block}`, 40, y);
      y += 6;

      const bodyRows = items.map((f) => [
        f.flatLabel || "—",
        fmtInrPdf(f.amount),
        f.method || "—",
        f.paidByName || "—",
        f.receiptNo || "—",
      ]);

      const blockTotal = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);

      autoTable(doc, {
        startY: y + 6,
        head: [["Flat", "Amount", "Method", "Received By", "Receipt"]],
        body: bodyRows,
        foot: [["", `Total: ${fmtInrPdf(blockTotal)}`, "", "", ""]],
        styles: {
          fontSize: 10,
          cellPadding: { top: 6, right: 8, bottom: 6, left: 8 },
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: palette.blue,
          textColor: palette.white,
          halign: "center",
          fontStyle: "bold",
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "right" },
          2: { halign: "left" },
          3: { halign: "left" },
          4: { halign: "left" },
        },
        footStyles: {
          fillColor: [250, 250, 250],
          textColor: palette.black,
          fontStyle: "bold",
          halign: "right",
        },
        theme: "striped",
        margin: { left: 40, right: 40 },
        tableWidth: "auto",
        showHead: "everyPage",
        showFoot: "lastPage",
        didDrawPage: function (data) {
          if (data.pageNumber > 1) {
            const left = data.settings.margin.left || 40;
            const top = 40;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text(`Block: ${block} (continued)`, left, top);
          }
        },
      });

      y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 14;
      if (idx < grouped.length - 1) y += 14;
    });

    return y;
  };

  // ✅ Committee — block-wise (kept within margins)
  const writeCommitteeByBlockTable = (doc, yStart, { forceNewPage = true } = {}) => {
    const committees = Array.isArray(event?.committee) ? event.committee : [];

    if (forceNewPage) doc.addPage();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Committee Members", 40, yStart);
    let y = yStart + 8;

    if (committees.length === 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text("No committee assigned.", 40, y + 12);
      return y + 30;
    }

    const grouped = groupCommitteeByBlock();

    grouped.forEach(([block, items], idx) => {
      // If we are too close to the bottom, start a fresh page
      const pageHeight = doc.internal.pageSize.getHeight();
      const bottomMargin = 60;
      if (y > pageHeight - bottomMargin) {
        doc.addPage();
        y = 40;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.text("Committee Members", 40, y);
        y += 8;
      }

      y += 8;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(`Block: ${block}`, 40, y);

      const bodyRows = items
        .sort((a, b) => cmpStr(a.flatLabel, b.flatLabel))
        .map((m) => [
          m.uid ? (m.residentName || m.uid) : (m.residentName || "—"),
          m.flatLabel || "—",
          m.roleLabel || m.role || "—",
          m.gender || "—",
        ]);

      autoTable(doc, {
        startY: y + 6,
        head: [["Resident", "Flat", "Role", "Gender"]],
        body: bodyRows,
        styles: {
          fontSize: 9,
          cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
          lineWidth: 0.1,
          overflow: "linebreak",
          cellWidth: "wrap",
        },
        headStyles: {
          fillColor: palette.blue,
          textColor: palette.white,
          halign: "center",
          fontStyle: "bold",
        },
        columnStyles: {
          0: { halign: "left",   cellWidth: 170 }, // Resident
          1: { halign: "left",   cellWidth: 70  }, // Flat
          2: { halign: "left",   cellWidth: 150 }, // Role
          3: { halign: "center", cellWidth: 55  }, // Gender
        },
        theme: "striped",
        margin: { left: 40, right: 40 },
        tableWidth: "wrap",        // keep inside margins
        pageBreak: "auto",
        rowPageBreak: "auto",
        tableLineWidth: 0,
        showHead: "everyPage",
        didDrawPage: function (data) {
          if (data.pageNumber > 1) {
            const left = data.settings.margin.left || 40;
            const top = 40;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text(`Block: ${block} (continued)`, left, top);
          }
        },
      });

      y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 10;
      if (idx < grouped.length - 1) y += 6;
    });

    return y;
  };

  const writeOtherCollectionsTable = (doc, yStart) => {
    if (!otherFunds || otherFunds.length === 0) return yStart;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Other Collections (Donations / Gifts / Sales)", 40, yStart);
    const startY = yStart + 10;

    const rows = otherFunds.map((f) => {
      const collectionName =
        (f.otherTitle && String(f.otherTitle).trim()) ||
        (f.otherType && String(f.otherType).trim()) ||
        "Other";
      const collectedBy =
        (f.receivedByName && String(f.receivedByName).trim()) ||
        (f.paidByName && String(f.paidByName).trim()) ||
        "—";
      return [collectionName, fmtInrPdf(f.amount), collectedBy];
    });

    const totalOther = otherFunds.reduce((s, it) => s + (Number(it.amount) || 0), 0);

    autoTable(doc, {
      startY,
      head: [["Collection", "Amount", "Collected By"]],
      body: rows,
      foot: [["", `Total: ${fmtInrPdf(totalOther)}`, ""]],
      styles: {
        fontSize: 10,
        cellPadding: { top: 6, right: 8, bottom: 6, left: 8 },
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: palette.blue,
        textColor: palette.white,
        halign: "center",
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "left", cellWidth: 240 },
        1: { halign: "right", cellWidth: 120 },
        2: { halign: "left", cellWidth: 160 },
      },
      footStyles: {
        fillColor: [250, 250, 250],
        textColor: palette.black,
        fontStyle: "bold",
      },
      theme: "striped",
      margin: { left: 40, right: 40 },
      tableWidth: "auto",
      showHead: "everyPage",
      showFoot: "lastPage",
    });

    return (doc.lastAutoTable && doc.lastAutoTable.finalY) || startY + 5;
  };

  const writeFooterSummaryBlock = (doc, yStart) => {
    // const fundsStr = fmtInrPdf(totalFunds);
    // const expStr = fmtInrPdf(totalExpenses);
    // const balStr = fmtInrPdf(balance);

    // doc.setFont("helvetica", "bold");
    // doc.setFontSize(12);
    // doc.text("Summary:", 40, yStart);

    // const startY = yStart + 12;

    // autoTable(doc, {
    //   startY,
    //   head: [],
    //   body: [[
    //     `Total Funds ${fundsStr}`,
    //     `Total Expenses ${expStr}`,
    //     `Balance ${balStr}`,
    //   ]],
    //   styles: {
    //     fontSize: 11,
    //     cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
    //     lineWidth: 0,
    //   },
    //   columnStyles: {
    //     0: { halign: "center" },
    //     1: { halign: "center" },
    //     2: { halign: "center" },
    //   },
    //   theme: "plain",
    //   margin: { left: 40, right: 40 },
    //   tableWidth: "auto",
    // });

    // const finalY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || startY + 14;
    // return finalY;
  };

  const writeEventLinkFooter = (doc) => {
    const url = getEventUrl();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const label = "VIEW FULL DETAILS WITH THIS LINK:";
    const linkText = `${label} ${url}`;

    const x = 40;
    const y = pageHeight - 30;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(25, 118, 210);
    doc.text(linkText, x, y, { maxWidth: pageWidth - 80 });

    try {
      const textWidth = doc.getTextWidth(linkText);
      if (typeof doc.link === "function") {
        doc.link(x, y - 8, Math.min(textWidth, pageWidth - 80), 12, { url });
      } else if (typeof doc.textWithLink === "function") {
        doc.textWithLink(linkText, x, y, { url });
      }
    } catch {
      // ignore link errors
    }
    doc.setTextColor(...palette.black);
  };

  // Generate PDF entry point: mode = 'short' | 'full'; includeCommittee (only for short)
  const generatePdf = async (mode, includeCommittee = false) => {
    try {
      setDownloading(true);

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      if (typeof autoTable !== "function") {
        alert("PDF table plugin not loaded. Please refresh and try again.");
        return;
      }

      // Header + summary band
      const afterHeaderY = writeHeader(doc);
      const afterSummaryY = writeSummaryBand(doc, afterHeaderY);

      if (mode === "short") {
        const afterBlocksY = writeBlockWiseSummaryTable(doc, afterSummaryY + 4);
        writeOtherCollectionsTable(doc, afterBlocksY + 16);

        const afterExpensesY = writeExpensesByCategoryTable(doc, 40, { forceNewPage: true });

        // ✅ Committee page (if selected)
        if (includeCommittee) {
          writeCommitteeByBlockTable(doc, 40, { forceNewPage: true });
        }

        // Final summary + link on the last page
        writeFooterSummaryBlock(doc, (afterExpensesY || 200) + 25);
        writeEventLinkFooter(doc);
      } else {
        // Full Statement flow
        const afterDetailsY = writeFullFundsByBlock(doc, afterSummaryY + 4);
        writeOtherCollectionsTable(doc, afterDetailsY + 16);
        const afterExpensesY = writeExpensesByCategoryTable(doc, 40, { forceNewPage: true });

        writeFooterSummaryBlock(doc, (afterExpensesY || 200) + 25);
        writeEventLinkFooter(doc);
      }

      doc.save(`Event_${event?.name || "Summary"}.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      alert(err?.message || "Failed to generate PDF.");
    } finally {
      setDownloading(false);
      setShowPdfModal(false);
    }
  };

  /* ----------------------- Excel Export (Full Statement) ----------------------- */
  const exportExcelFullStatement = () => {
    try {
      const wb = XLSX.utils.book_new();

      // 1) Funds — Detailed by Block
      const fundsDetailedRows = [
        ["Block", "Flat", "Amount", "Method", "Received By", "Receipt"]
      ];
      residentFunds.forEach((f) => {
        fundsDetailedRows.push([
          f.blockName || "—",
          f.flatLabel || "—",
          Number(f.amount) || 0,
          f.method || "—",
          f.paidByName || "—",
          f.receiptNo || "—",
        ]);
      });
      const wsFundsDetailed = XLSX.utils.aoa_to_sheet(fundsDetailedRows);

      // 2) Block-wise Summary
      const blockSummaryRows = [["Block", "Total Amount", "Entries"]];
      groupResidentFundsByBlock().forEach(([block, items]) => {
        const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
        blockSummaryRows.push([block, total, items.length]);
      });
      const wsBlockSummary = XLSX.utils.aoa_to_sheet(blockSummaryRows);

      // 3) Other Collections
      const otherRows = [["Collection", "Amount", "Collected By"]];
      otherFunds.forEach((f) => {
        const collectionName =
          (f.otherTitle && String(f.otherTitle).trim()) ||
          (f.otherType && String(f.otherType).trim()) ||
          "Other";
        const collectedBy =
          (f.receivedByName && String(f.receivedByName).trim()) ||
          (f.paidByName && String(f.paidByName).trim()) ||
          "—";
        otherRows.push([collectionName, Number(f.amount) || 0, collectedBy]);
      });
      const wsOther = XLSX.utils.aoa_to_sheet(otherRows);

      // 4) Expenses — Category-wise (with compact purposes)
      const catRows = [["Category", "Total Amount", "Entries", "Purposes (compact)"]];
      groupExpensesByCategory().forEach(([cat, items]) => {
        const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
        const purposesText = items
          .map((it) => (it.purpose || "—").toString().trim())
          .filter((p) => p && p !== "—")
          .join(" • ");
        catRows.push([cat, total, items.length, purposesText || "—"]);
      });
      const wsCat = XLSX.utils.aoa_to_sheet(catRows);

      // 5) Expenses — Details
      const expRows = [["Purpose", "Category", "Amount", "Paid By", "Receipt", "Created"]];
      expenses.forEach((e) => {
        const paidBy =
          e.paidByUid ? (e.paidByName || e.paidByUid) : (e.paidByName || "—");
        expRows.push([
          e.purpose || "—",
          e.category || "—",
          Number(e.amount) || 0,
          paidBy,
          e.receiptNo || "—",
          e.createdAt?.toDate ? e.createdAt.toDate().toLocaleString() : "—",
        ]);
      });
      const wsExp = XLSX.utils.aoa_to_sheet(expRows);

      // Append sheets
      XLSX.utils.book_append_sheet(wb, wsFundsDetailed, "Funds_Detailed_By_Block");
      XLSX.utils.book_append_sheet(wb, wsBlockSummary, "Block_Wise_Summary");
      XLSX.utils.book_append_sheet(wb, wsOther, "Other_Collections");
      XLSX.utils.book_append_sheet(wb, wsCat, "Expenses_By_Category");
      XLSX.utils.book_append_sheet(wb, wsExp, "Expenses_Details");

      XLSX.writeFile(wb, `Event_${event?.name || "Full_Statement"}.xlsx`);
    } catch (err) {
      console.error("Excel export error:", err);
      alert(err?.message || "Failed to export Excel.");
    }
  };

  /* ----------------------- Render ----------------------- */

  const blockSummaryRows = useMemo(() => {
    return groupResidentFundsByBlock().map(([block, items]) => {
      const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
      return { block, total, count: items.length };
    });
  }, [residentFunds]);

  return (
    <div>
      <Card className="mb-3">
        <Card.Body>
          {/* Top section with aligned summary */}
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
            <div className="me-md-4 mb-3 mb-md-0">
              <h5 className="mb-1">
                {event?.name || "Event"}{" "}
                <span className="badge bg-info ms-2">{event?.status || "—"}</span>
              </h5>
              <div className="text-muted mb-2">{community?.name || "—"}</div>
              <div><strong>Date:</strong> {event?.date || "—"}</div>
              <div><strong>Description:</strong> {event?.description || "—"}</div>
            </div>

            {/* Summary band (UI) */}
            <div className="w-100 w-md-auto">
              <div className="p-3 rounded-3 border bg-light">
                <div className="row g-3 text-center text-md-start">
                  <div className="col-12 col-md">
                    <div className="small text-muted">Total Funds</div>
                    <div className="fs-5 fw-semibold text-success">{fmtInr(totalFunds)}</div>
                  </div>
                  <div className="col-12 col-md">
                    <div className="small text-muted">Total Expenses</div>
                    <div className="fs-5 fw-semibold text-danger">{fmtInr(totalExpenses)}</div>
                  </div>
                  <div className="col-12 col-md">
                    <div className="small text-muted">Balance</div>
                    <div
                      className={`fs-5 fw-semibold ${
                        balance >= 0 ? "text-success" : "text-danger"
                      }`}
                    >
                      {fmtInr(balance)}
                    </div>
                  </div>
                </div>
                <div className="text-end mt-2 d-flex gap-2 justify-content-end">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={exportExcelFullStatement}
                    disabled={!eventId}
                  >
                    Export Excel (Full Statement)
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowPdfModal(true)}
                    disabled={!eventId}
                  >
                    Download PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Block-wise Collection table below the summary */}
      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-3">Block‑wise Collection</h6>
          <Table bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Block</th>
                <th className="text-end">Total Amount (₹)</th>
                <th className="text-center">Entries</th>
              </tr>
            </thead>
            <tbody>
              {blockSummaryRows.length === 0 ? (
                <tr><td colSpan={3} className="text-center">No resident collections yet</td></tr>
              ) : (
                blockSummaryRows.map((r, idx) => (
                  <tr key={`block-summary-${r.block}-${idx}`}>
                    <td>{r.block}</td>
                    <td className="text-end">{fmtInr(r.total)}</td>
                    <td className="text-center">{r.count}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Modal: Choose PDF type (Short Summary choices in CAPITAL LETTERS) */}
      <Modal show={showPdfModal} onHide={() => setShowPdfModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Download PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <strong>Select the type of report to download:</strong>
          </div>

          <div className="mb-2">Short Summary options:</div>
          <div className="d-flex flex-column gap-2 mb-3">
            <Button
              variant="primary"
              onClick={() => generatePdf("short", true)}
              disabled={downloading}
            >
              {downloading ? "Building…" : "SHORT SUMMARY — WITH COMMITTEE MEMBERS"}
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => generatePdf("short", false)}
              disabled={downloading}
            >
              {downloading ? "Building…" : "SHORT SUMMARY — WITHOUT COMMITTEE MEMBERS"}
            </Button>
          </div>

          <div className="mb-2">Or export the full statement:</div>
          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="secondary"
              onClick={() => setShowPdfModal(false)}
              disabled={downloading}
            >
              Cancel
            </Button>
            <Button
              variant="outline-dark"
              onClick={() => generatePdf("full")}
              disabled={downloading}
            >
              {downloading ? "Building…" : "Full Statement"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
